import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import api, { authService } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in
  useEffect(() => {
    const checkLoggedIn = async () => {
      console.log('Checking if user is logged in...');
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      console.log('Stored token exists:', !!storedToken);
      console.log('Stored user exists:', !!storedUser);
      
      if (storedToken) {
        // If we have a stored user, use it directly (avoid unnecessary API call)
        if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            console.log('Setting user data from localStorage:', userData);
            setUser(userData);
            setIsAuthenticated(true);
            setLoading(false);
            return;
          } catch (e) {
            console.error('Error parsing stored user data:', e);
          }
        }
        try {
          // Get user data using the configured API service
          console.log('Fetching user data with token...');
          const res = await authService.getMe();
          console.log('getMe response:', res);
          
          if (res.data.success) {
            // Get user data from the correct property (might be res.data.data or res.data.user depending on API)
            const userData = res.data.user || res.data.data;
            console.log('Setting user data in context:', userData);
            setUser(userData);
            setIsAuthenticated(true);
          } else {
            console.log('Token valid but unsuccessful response');
            // Token may be expired or invalid - don't show toast during initial auth check
            logout(false);
          }
        } catch (err) {
          console.error('Error checking authentication:', err);
          console.log('Calling logout due to authentication error');
          // Don't show toast during initial auth check
          logout(false);
        }
      } else {
        console.log('No token found, user is not authenticated');
      }
      setLoading(false);
    };

    checkLoggedIn();
  }, []); // Run once at mount, not on token changes

  // Register user
  const register = async (userData) => {
    try {
      const res = await authService.register(userData);
      
      if (res.data.success) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        setIsAuthenticated(true);
        toast.success('Registration successful!');
        return true;
      }
    } catch (err) {
      const message = err.response && err.response.data.message 
        ? err.response.data.message 
        : 'Registration failed. Please try again.';
      toast.error(message);
      return false;
    }
  };

  // Login user
  const login = async (userData) => {
    try {
      console.log('Attempting login with:', { email: userData.email, passwordLength: userData.password?.length });
      
      // Get the current API URL from the api service
      const apiUrl = api.defaults.baseURL;
      console.log('API URL:', apiUrl);
      
      // Clear any previous auth state before attempting login
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      
      const res = await authService.login(userData);
      console.log('Login API response:', res);
      
      if (res && res.data && res.data.success) {
        console.log('Login successful - storing token and user data');
        // Store auth data in localStorage
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('isAuthenticated', 'true');
        
        // Update state
        setToken(res.data.token);
        setUser(res.data.user);
        setIsAuthenticated(true);
        
        toast.success('Login successful!');
        console.log('Auth context updated, returning true');
        return true;
      } else {
        console.log('Login failed - success flag not present in response');
        toast.error('Login failed. Invalid response from server.');
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      console.log('Login error response:', err.response);
      const message = err.response && err.response.data.message 
        ? err.response.data.message 
        : 'Login failed. Please try again.';
      toast.error(message);
      return false;
    }
  };

  // Logout user
  const logout = (showToast = true) => {
    console.log('Logging out user...');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    if (showToast) {
      toast.info('You have been logged out');
    }
  };

  // Check if token is expired
  const isTokenExpired = () => {
    if (!token) return true;
    
    try {
      // Using JWT expiration validation for all tokens
      console.log('Checking token expiration');
      
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      console.log('Token expiration check:', { 
        exp: decoded.exp, 
        current: currentTime, 
        isExpired: decoded.exp < currentTime 
      });
      
      if (decoded.exp < currentTime) {
        console.log('Token is expired, logging out');
        logout(false); // Don't show toast for expiration during checks
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Error decoding token:', err);
      logout(false); // Don't show toast for JWT errors during checks
      return true;
    }
  };

  // Check if user has a specific role
  const hasRole = (roles) => {
    console.log('hasRole check with user:', user);
    console.log('looking for roles:', roles);
    
    // First check context user
    if (user && roles.includes(user.role)) {
      console.log('User has role via context:', user.role);
      return true;
    }
    
    // Fallback to localStorage if context check fails
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        console.log('Checking localStorage user role:', userData.role);
        return roles.includes(userData.role);
      }
    } catch (e) {
      console.error('Error checking role from localStorage:', e);
    }
    
    return false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        register,
        login,
        logout,
        isTokenExpired,
        hasRole
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;